import React, { PropsWithChildren } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { appRoutes } from './_routes/router'
import { AppRouteError } from './AppRouteError'

const BASENAME = (process.env.MK_BASE_PATH || '/mk').replace(/\/$/, '')

export interface AppRouterProps {}

/**
 * AppRouter component - Data Router wrapper for React Router v6+
 *
 * This component creates a data router using `createBrowserRouter` which enables
 * advanced features like `useBlocker` for blocking navigation transitions.
 *
 * The router structure:
 * - Root route ('/') renders the `children` prop (providers, error boundaries, etc.)
 * - Root `errorElement` redirects OAuth callback failures with loginError instead of React Router’s default fatal UI (CHP2-14792)
 * - Child routes from `appRoutes` are nested under the root
 * - Children must include an `<Outlet />` component where nested routes will render
 *
 * @example
 * <AppRouter>
 *   <Providers>
 *     <Outlet /> {/* Routes render here *\/}
 *   </Providers>
 * </AppRouter>
 */
export const AppRouter: React.FC<PropsWithChildren<AppRouterProps>> = ({ children }) => {
  const router = createBrowserRouter(
    [
      {
        path: '/',
        element: children,
        errorElement: <AppRouteError />,
        children: appRoutes,
      },
    ],
    {
      basename: BASENAME,
      future: {
        v7_relativeSplatPath: true,
      },
    },
  )

  return (
    <RouterProvider
      router={router}
      future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
    />
  )
}
